import { Sparkles, Disc3, Ticket } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { PageHeader } from '@/components/dashboard/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

type Game = 'grattage' | 'roulette' | 'tombola';

const games: { id: Game; label: string; desc: string; icon: React.ReactNode }[] = [
  {
    id: 'grattage',
    label: 'Grattage',
    desc: 'Carte à gratter, révélation tactile.',
    icon: <Sparkles className="size-5" />,
  },
  {
    id: 'roulette',
    label: 'Roulette',
    desc: 'Roue de la fortune animée.',
    icon: <Disc3 className="size-5" />,
  },
  {
    id: 'tombola',
    label: 'Tombola',
    desc: 'Tirage au sort programmé.',
    icon: <Ticket className="size-5" />,
  },
];

const PartenaireNouvelle = () => {
  const [game, setGame] = useState<Game>('grattage');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success('Campagne créée — prête à publier.');
  };

  return (
    <DashboardLayout
      role="partenaire"
      user={{ name: 'Marie Koné', subtitle: 'Orange CI', initials: 'MK' }}
    >
      <PageHeader
        title="Nouvelle campagne"
        subtitle="10 minutes pour créer une activation phygitale prête à publier."
      />

      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="font-display text-lg font-bold">1. Type de jeu</h3>
            <p className="mb-4 text-xs text-muted-foreground">
              Tu pourras combiner plusieurs jeux par la suite (plan Pro).
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {games.map(g => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGame(g.id)}
                  className={cn(
                    'rounded-2xl border p-4 text-left transition-all',
                    game === g.id
                      ? 'border-primary bg-primary/5 shadow-cta'
                      : 'border-border bg-card hover:bg-muted/40'
                  )}
                >
                  <span
                    className={cn(
                      'grid size-9 place-items-center rounded-xl',
                      game === g.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    )}
                  >
                    {g.icon}
                  </span>
                  <p className="mt-3 font-semibold">{g.label}</p>
                  <p className="text-xs text-muted-foreground">{g.desc}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="font-display text-lg font-bold">2. Informations campagne</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="name">Nom de la campagne</Label>
                <Input
                  id="name"
                  placeholder="Ex. Festival Orange Abidjan 2026"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="brand">Marque / Annonceur</Label>
                <Input id="brand" placeholder="Orange CI" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="city">Ville</Label>
                <Input id="city" placeholder="Abidjan" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="start">Début</Label>
                <Input id="start" type="date" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="end">Fin</Label>
                <Input id="end" type="date" className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="desc">Description (visible par les participants)</Label>
                <Textarea
                  id="desc"
                  rows={3}
                  placeholder="Tente ta chance et gagne des billets VIP…"
                  className="mt-1.5"
                />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="font-display text-lg font-bold">3. Lots & probabilités</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="prize">Nom du lot</Label>
                <Input id="prize" placeholder="Billet VIP concert" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="qty">Quantité</Label>
                <Input id="qty" type="number" placeholder="200" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="prob">Probabilité (%)</Label>
                <Input id="prob" type="number" placeholder="30" className="mt-1.5" />
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" className="mt-4">
              + Ajouter un lot
            </Button>
          </section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="font-display text-lg font-bold">Aperçu participant</h3>
            <div className="mt-4 overflow-hidden rounded-xl bg-gradient-primary p-6 text-primary-foreground shadow-elevated">
              <p className="text-xs uppercase tracking-widest opacity-80">Aperçu</p>
              <p className="mt-2 font-display text-2xl font-extrabold">Tente ta chance !</p>
              <p className="mt-1 text-sm opacity-90">{games.find(g => g.id === game)!.desc}</p>
              <div className="mt-5 grid place-items-center rounded-xl bg-foreground/10 py-8 text-center">
                <div className="grid size-14 place-items-center rounded-2xl bg-background text-primary">
                  {games.find(g => g.id === game)!.icon}
                </div>
                <p className="mt-3 text-xs uppercase tracking-widest opacity-90">
                  {games.find(g => g.id === game)!.label}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="text-sm font-semibold">Estimation</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex justify-between text-muted-foreground">
                <span>Participations attendues</span>
                <span className="font-medium text-foreground">2 500 – 4 000</span>
              </li>
              <li className="flex justify-between text-muted-foreground">
                <span>Coût estimé</span>
                <span className="font-medium text-foreground">450 000 FCFA</span>
              </li>
              <li className="flex justify-between text-muted-foreground">
                <span>Leads opt-in projetés</span>
                <span className="font-medium text-foreground">~ 1 800</span>
              </li>
            </ul>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1">
              Brouillon
            </Button>
            <Button type="submit" className="flex-1 shadow-cta">
              Créer & publier
            </Button>
          </div>
        </aside>
      </form>
    </DashboardLayout>
  );
};

export default PartenaireNouvelle;
